import { apiRequest } from "./apiClient";

/**
 * @typedef {Object} OpeningHoursViolation
 * @property {"closed_day"|"outside_hours"|"unclear"} type - Type of violation
 * @property {string} description - Human-readable description
 * @property {"CRITICAL"|"HIGH"|"MEDIUM"|"LOW"} severity - Severity level
 */

/**
 * @typedef {Object} QuestionAnalysis
 * @property {string} question - The question text
 * @property {boolean} information_obtained - Whether the information was obtained
 * @property {"asked_by_agent"|"volunteered_by_caller"|"not_obtained"} how_obtained - How information was obtained
 * @property {boolean} was_asked - Whether the question was asked by agent
 * @property {boolean} should_have_asked - Whether the agent should have asked
 * @property {boolean} answer_received - Whether an answer was received
 * @property {boolean} rules_followed - Whether rules were followed
 * @property {string} evidence - Evidence from the conversation
 * @property {"CRITICAL"|"IMPORTANT"|"OPTIONAL"} criticality - Question criticality
 * @property {string} [notes] - Optional notes about this question
 */

/**
 * @typedef {Object} PromptComplianceAnalysis
 * @property {string[]} key_rules_from_prompt - All rules extracted from prompt
 * @property {string[]} rules_followed - Rules that were followed
 * @property {string[]} rules_violated - Rules that were violated
 * @property {string[]} violation_details - Detailed violation descriptions
 */

/**
 * @typedef {Object} AppointmentDetails
 * @property {string|null} day - Day of week
 * @property {string|null} time - Time of day
 * @property {string|null} date - Full date if provided
 */

/**
 * @typedef {Object} OpeningHoursAnalysis
 * @property {boolean} appointment_discussed - Whether appointment was discussed
 * @property {AppointmentDetails} appointment_details - Appointment details
 * @property {string} clinic_hours_for_day - Clinic hours for that day
 * @property {boolean|null} is_within_hours - Whether appointment is within hours
 * @property {OpeningHoursViolation[]} violations - List of violations
 */

/**
 * @typedef {Object} CallRating
 * @property {string} id - Rating UUID
 * @property {string} conversation_id - Conversation UUID
 * @property {string} clinic_id - Clinic UUID
 * @property {number} overall_score - Overall score (1-5)
 * @property {Object} scores - Dimension scores
 * @property {number} scores.question_compliance - Question compliance score
 * @property {number} scores.rule_compliance - Rule compliance score
 * @property {number} scores.information_accuracy - Information accuracy score
 * @property {number} scores.goal_achievement - Goal achievement score
 * @property {number} scores.conversation_quality - Conversation quality score
 * @property {number} scores.error_handling - Error handling score
 * @property {string} call_outcome - Call outcome
 * @property {string[]} issues_found - Issues found
 * @property {string[]} strengths - Strengths identified
 * @property {string[]} improvement_suggestions - Improvement suggestions
 * @property {string} gpt_reasoning - Detailed AI reasoning
 * @property {boolean} requires_review - Whether review is required
 * @property {string[]} review_reasons - Reasons for review
 * @property {string} rating_model - Model used for rating
 * @property {string} rated_at - Rating timestamp
 * @property {OpeningHoursAnalysis} opening_hours_analysis - Opening hours analysis
 * @property {OpeningHoursViolation[]} opening_hours_violations - Opening hours violations
 * @property {string[]} questions_asked - Questions that were asked
 * @property {string[]} questions_missed - Questions that were missed
 * @property {QuestionAnalysis[]} questions_analysis - Detailed question analysis
 * @property {string[]} rule_violations - Rule violations
 * @property {PromptComplianceAnalysis} prompt_compliance_analysis - Prompt compliance
 * @property {Object} full_analysis - Complete analysis object
 */

/**
 * Fetch call rating by conversation ID (admin only)
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<{data: CallRating}>} Rating data
 */
export const fetchCallRating = async (conversationId) => {
  return apiRequest({
    method: "GET",
    endpoint: `/call-ratings/${conversationId}`,
  });
};

/**
 * Fetch all call ratings for a clinic (admin only)
 * @param {string} clinicId - The clinic UUID
 * @param {number} limit - Max results (default 100)
 * @param {number} offset - Skip results (default 0)
 * @returns {Promise<{data: CallRating[], total: number}>} List of ratings
 */
export const fetchCallRatingsByClinic = async (clinicId, limit = 100, offset = 0) => {
  return apiRequest({
    method: "GET",
    endpoint: `/call-ratings?clinic_id=${clinicId}&limit=${limit}&offset=${offset}`,
  });
};

/**
 * Fetch rating statistics for a clinic (admin only)
 * @param {string} clinicId - The clinic UUID
 * @returns {Promise<{data: Object}>} Rating stats
 */
export const fetchRatingStats = async (clinicId) => {
  return apiRequest({
    method: "GET",
    endpoint: `/call-ratings/stats/${clinicId}`,
  });
};

/**
 * Fetch ratings needing review (admin only)
 * @param {string} clinicId - Optional clinic UUID filter
 * @param {number} limit - Max results (default 50)
 * @returns {Promise<{data: CallRating[]}>} List of ratings needing review
 */
export const fetchRatingsNeedingReview = async (clinicId = null, limit = 50) => {
  const params = new URLSearchParams({ limit });
  if (clinicId) params.append("clinic_id", clinicId);
  return apiRequest({
    method: "GET",
    endpoint: `/call-ratings/needs-review?${params.toString()}`,
  });
};

/**
 * Mark a rating as reviewed (admin only)
 * @param {string} ratingId - The rating UUID
 * @returns {Promise<{data: CallRating}>} Updated rating
 */
export const markRatingReviewed = async (ratingId) => {
  return apiRequest({
    method: "POST",
    endpoint: `/call-ratings/${ratingId}/review`,
  });
};
